import React, {memo, useMemo} from "react";
import {NavLink, useLocation} from "react-router-dom";
import {isEmpty} from "es-toolkit/compat";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import {
  faBookOpen,
  faCalendar,
  faCartShopping,
  faCircleNotch,
  faCommentQuestion,
  faHouse,
  faQuestion,
} from "@fortawesome/pro-regular-svg-icons";
import {faHouse as faHouseSolid} from "@fortawesome/pro-solid-svg-icons";

import styles from "./QuickLinksMobile.module.scss";
import {useWhiteLabelTheme} from "@/hooks/useWhiteLabelTheme/useWhiteLabelTheme";
import {config} from "@/config";

const ICON_MAP = {
  faCalendar: faCalendar,
  faCommentQuestion: faCommentQuestion,
  faHouse: faHouse,
  faHouseSolid: faHouseSolid,
  faCircleNotch: faCircleNotch,
  faCartShopping: faCartShopping,
  faBookOpen: faBookOpen,
};

export default memo(function QuickLinksMobile({routes: defaultRoutes, ...rest}) {
  const {pathname} = useLocation();
  const {themeConfig} = useWhiteLabelTheme();

  const routes = useMemo(() => {
    if (!isEmpty(themeConfig?.mobileNav)) {
      return themeConfig?.mobileNav.map((item, index) => ({
        ...item,
        icon: item.icon ? (
          <FontAwesomeIcon
            icon={ICON_MAP[item.icon] || faQuestion}
            size="2xl"
            style={item.icon === "faCircleNotch" ? {transform: "rotate(180deg)"} : {}}
          />
        ) : null,
        activeIcon: item.activeIcon ? (
          <FontAwesomeIcon
            icon={ICON_MAP[item.activeIcon] || faQuestion}
            size="2xl"
            style={item.icon === "faCircleNotch" ? {transform: "rotate(180deg)"} : {}}
          />
        ) : null,
        order: item.order || index + 1,
      }));
    }

    return defaultRoutes;
  }, [defaultRoutes, themeConfig]);

  const position = routes?.findIndex(route => {
    if (route.path === "/") return route.path === pathname;

    if (pathname.includes("/health-score") && route.path === "/results") {
      return "results".indexOf(route.path.slice(1)) !== -1;
    }

    return pathname.split("/").indexOf(route.path.slice(1)) !== -1;
  });

  return (
    <>
      <nav {...rest} className={`${styles.mobileNavbar} d-flex d-md-none mx-auto`}>
        <span
          className={`${styles.bar}`}
          style={{
            width: `${100 / routes.length}%`,
            transform: `translate3d(${position * 100}%, 0px, 0px)`,
          }}
        />

        {routes?.map((route, index) => {
          let isActive = route.path === pathname;
          const icon = isActive && route?.activeIcon ? route?.activeIcon : route?.icon;
          let target = route.target ? route.target : "_self";

          if (pathname.includes("health-score") && route.path === "/results") {
            isActive = true;
          }

          let url = route.path;
          let routeName = route?.name;
          const isExternalLink = url.includes("http");

          if (isExternalLink) {
            target = "_blank";
            url = new URL(route.path);

            if (config.isNativeApp) {
              url.searchParams.set("external", "true");
            }

            url = url.toString();

            return (
              <a
                key={index}
                href={url}
                target="_blank"
                className={`${styles.navItem} ${isActive ? styles.active : ""}`}>
                {icon}
                {routeName}
              </a>
            );
          }

          return (
            <NavLink
              target={target}
              key={index}
              className={({isActive}) => {
                if (pathname.includes("health-score") && route.path === "/results") {
                  return `${styles.active} ${styles.navItem}`;
                }
                return isActive ? `${styles.active} ${styles.navItem}` : `${styles.navItem}`;
              }}
              to={url}>
              {icon}
              {routeName}
            </NavLink>
          );
        })}
      </nav>
    </>
  );
});
